import { Divider, Text, useColors } from "@certn/cascadia-react";
import { useGoogleAnalytics } from "@certn/utils";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { useSettingsDropdownStyles } from "./styles";
import { useUser } from "../../../hooks/api/useUser";
import { useAuth } from "../../../pages/auth/hooks";
import { UserProfileName } from "../../../types";
import { getPrimaryName } from "../../../utils/user-utils";
import { Menu } from "../../Menu";

const formatUserName = (userName?: UserProfileName) => {
  if (!userName) {
    return "-";
  }

  return `${userName.first_name} ${userName.last_name?.charAt(0)}.`;
};

const LinkItem: React.FC<{
  to: string;
  children: React.ReactNode;
  testID: string;
}> = ({ to, children, testID = "header-settings-dropdown-button" }) => {
  const styles = useSettingsDropdownStyles();
  const colors = useColors();

  return (
    <Link
      to={to}
      className={css(styles.linkItem, {
        "&:hover": {
          background: colors["background-accent-neutral-weak"],
        },
      })}
    >
      <Text as="h4" testID={testID} style="input/text-field">
        {children}
      </Text>
    </Link>
  );
};

export function SettingsDropdown() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { user } = useUser();
  const styles = useSettingsDropdownStyles();
  const userName = user && getPrimaryName(user);
  const { gaTrackEvent } = useGoogleAnalytics();

  const handleLogout = () => {
    logout();
    gaTrackEvent("logout");
    navigate("/auth/welcome");
    window.location.reload();
  };

  return (
    <Menu label={formatUserName(userName)}>
      <div className={styles.nameSection}>
        <Text
          truncate
          style="global/heading-medium"
          testID="settings-dropdown-menu-button"
        >
          {userName?.first_name} {userName?.last_name || ""}
        </Text>
        <Text truncate style="global/body-secondary">
          {user?.email}
        </Text>
      </div>

      <div className={styles.divider}>
        <Divider />
      </div>

      <div className={styles.section}>
        <LinkItem to="/" testID="settings-dashboard-dropdown-bottom">
          {t("Dashboard")}
        </LinkItem>
        <LinkItem
          to="/settings/personal-info"
          testID="settings-personal-info-dropdown-button"
        >
          {t("Personal info")}
        </LinkItem>
        <LinkItem
          to="/settings/preferences"
          testID="settings-preferences-dropdown-button"
        >
          {t("Preferences")}
        </LinkItem>
        <LinkItem
          to="/settings/notifications"
          testID="settings-notifications-dropdown-button"
        >
          {t("Notifications")}
        </LinkItem>
        <LinkItem
          to="/settings/help-and-privacy"
          testID="settings-help-and-privacy-dropdown-button"
        >
          {t("Help & privacy")}
        </LinkItem>
      </div>

      <div className={styles.divider}>
        <Divider />
      </div>

      <div className={styles.section}>
        <button className={styles.linkItem} onClick={handleLogout}>
          <Text
            as="h4"
            style="input/text-field"
            testID="sign-out-button-dropdown"
          >
            {t("Sign out")}
          </Text>
        </button>
      </div>
    </Menu>
  );
}
